import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ALLIANCE_APPLICATION_MESSAGE_MAX_LENGTH } from '@kaeliux/shared';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';

interface AllianceApplyModalProps {
    /** The alliance being applied to; `null` closes the modal. */
    alliance: { id: string; name: string; tag: string } | null;
    isSubmitting: boolean;
    onSubmit: (allianceId: string, message: string) => void;
    onCancel: () => void;
}

/**
 * Compose the note that rides along with a join application. Modelled on
 * `EditTextModal`, but with a textarea and no minimum length — the message is
 * optional (`ApplyToAllianceSchema` defaults it to `''`), so "Send
 * application" stays enabled on an empty field.
 */
export default function AllianceApplyModal({
    alliance,
    isSubmitting,
    onSubmit,
    onCancel,
}: AllianceApplyModalProps) {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [message, setMessage] = useState('');
    const allianceId = alliance?.id ?? null;

    // Reset keyed on WHICH alliance is open, and nothing else. Folding the
    // Escape listener in here too would drag `onCancel` into the deps — it is
    // a fresh arrow on every parent render, so the effect would re-run on any
    // re-render (a background refetch, say) and wipe what the player had
    // typed mid-sentence.
    useEffect(() => {
        if (!allianceId) return;
        setMessage('');
        inputRef.current?.focus();
    }, [allianceId]);

    function handleSubmit(e: FormEvent) {
        e.preventDefault();
        if (isSubmitting || !alliance) return;
        onSubmit(alliance.id, message.trim());
    }

    return (
        <Modal
            open={Boolean(alliance)}
            onClose={onCancel}
            title={`Apply to [${alliance?.tag}] ${alliance?.name}`}
            data-testid="alliance-apply-modal"
        >
            <form onSubmit={handleSubmit}>
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                        <label htmlFor="alliance-apply-message" className="text-label text-content-secondary">
                            Message to the recruiters (optional)
                        </label>
                        <textarea
                            ref={inputRef}
                            id="alliance-apply-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            maxLength={ALLIANCE_APPLICATION_MESSAGE_MAX_LENGTH}
                            placeholder="Why you want to join, and what you bring."
                            className="min-h-24 rounded-control bg-space-950 border border-space-600 px-3 py-2 text-body text-content-primary placeholder:text-content-muted focus-visible:outline-none focus-visible:border-hairline-strong focus-visible:ring-2 focus-visible:ring-hairline-strong/40"
                        />
                        <p className="text-micro text-content-muted self-end">
                            {message.length} / {ALLIANCE_APPLICATION_MESSAGE_MAX_LENGTH}
                        </p>
                    </div>
                    <div className="flex gap-3 justify-end">
                        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" size="sm" loading={isSubmitting}>
                            Send application
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    );
}
